import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
  id: string
  username: string
  email: string
  name: string
  avatar?: string
  role: 'guest' | 'user' | 'admin'
  provider: 'local' | 'google'
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  setToken: (token: string) => void
  setAuthData: (user: User, token: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      
      login: async (email: string, password: string) => {
        try {
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          })
          
          if (!response.ok) {
            throw new Error('Identifiants invalides')
          }
          
          const data = await response.json()
          
          set({
            user: data.user,
            token: data.token,
            isAuthenticated: true,
          })
        } catch (error) {
          console.error('Erreur de connexion:', error)
          throw error
        }
      },

      loginWithGoogle: async () => {
        try {
          // * Get Google OAuth URL from backend
          const response = await fetch('/api/auth/google/url')
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            if (response.status === 500 && errorData.error?.includes('not configured')) {
              throw new Error('La connexion Google n\'est pas configurée sur le serveur. Veuillez contacter l\'administrateur.')
            }
            throw new Error(errorData.error || 'Impossible d\'obtenir l\'URL d\'authentification Google')
          }
          
          const data = await response.json()
          if (!data.authUrl) {
            throw new Error('URL d\'authentification Google non disponible')
          }
          // * Redirect to Google OAuth
          window.location.href = data.authUrl
        } catch (error) {
          console.error('Erreur de connexion Google:', error)
          throw error
        }
      },

      setAuthData: (user: User, token: string) => {
        set({
          user,
          token,
          isAuthenticated: true,
        })
      },
      
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        })
      },
      
      setUser: (user: User) => {
        set({ user })
      },
      
      setToken: (token: string) => {
        set({ token, isAuthenticated: true })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
